<template>
  <div class="landing-domain-settings space-y-5">
    <section class="card p-4 space-y-1" aria-labelledby="madely-address-heading">
      <h3 id="madely-address-heading" class="font-medium">
        Your Made.ly address
      </h3>
      <p class="break-all font-mono text-sm" data-test="default-hostname">
        {{ site.default_hostname || 'Assigned once hosting is ready' }}
      </p>
      <p class="text-sm" style="color: var(--text-secondary);" data-test="hosting-state">
        {{ hostingMessage }}
      </p>
    </section>

    <section class="card p-4 space-y-2" aria-labelledby="page-paths-heading">
      <h3 id="page-paths-heading" class="font-medium">
        Page addresses
      </h3>
      <p v-if="!pages.length" class="text-sm" style="color: var(--text-secondary);">
        No pages on this site yet.
      </p>
      <ul v-else class="space-y-2 text-sm list-none p-0 m-0">
        <li
          v-for="row in pages"
          :key="row.id"
          class="flex flex-col gap-1 border-b pb-2 sm:flex-row sm:items-center sm:justify-between"
          style="border-color: var(--border-primary, #e5e7eb);"
          :data-test="`path-${row.id}`">
          <span><span class="font-medium">{{ row.title }}</span> <span class="font-mono break-all">{{ row.path }}</span></span>
          <span class="break-all" style="color: var(--text-secondary);">
            {{ row.status === 'published' && row.canonical_url ? row.canonical_url : (row.status === 'archived' ? 'Archived' : 'Not published yet') }}
          </span>
        </li>
      </ul>
    </section>

    <section class="card p-4 space-y-3" aria-labelledby="custom-domains-heading">
      <h3 id="custom-domains-heading" class="font-medium">
        Your own domains
      </h3>
      <p v-if="!domains.length" class="text-sm" style="color: var(--text-secondary);">
        You haven't added a domain. Your pages work on your Made.ly address without one.
      </p>
      <ul v-else class="space-y-3 list-none p-0 m-0">
        <li
          v-for="domain in domains"
          :key="domain.id"
          class="rounded border p-3 space-y-2 text-sm"
          style="border-color: var(--border-primary, #e5e7eb);"
          :data-test="`domain-${domain.id}`">
          <div class="flex flex-wrap items-center justify-between gap-2">
            <span class="font-mono break-all font-medium">{{ domain.hostname }}</span>
            <span class="rounded-full px-2 py-0.5 text-xs" :class="badgeClass(domain.state)">
              {{ domain.primary ? 'Main address · ' : '' }}{{ STATE_LABELS[domain.state] || 'Unknown' }}
            </span>
          </div>
          <p style="color: var(--text-secondary);">
            DNS: {{ stepLabel(domain.dns_state) }} · Ownership: {{ stepLabel(domain.ownership_state) }} ·
            Certificate: {{ stepLabel(domain.tls_state) }}
          </p>
          <div v-if="domain.state !== 'active' && domain.instructions && domain.instructions.length" class="space-y-1">
            <p>Add this record at your DNS provider:</p>
            <div
              v-for="(record, index) in domain.instructions"
              :key="index"
              class="grid gap-1 rounded p-2 font-mono text-xs sm:grid-cols-3"
              style="background: var(--bg-surface-sunken, rgba(127, 127, 127, 0.1));">
              <span>Type: {{ record.record_type }}</span>
              <span class="break-all">Name: {{ record.name }}</span>
              <span class="break-all">Value: <code class="select-all">{{ record.value || 'provided by your registrar' }}</code></span>
            </div>
          </div>
          <div v-if="!readOnly" class="flex flex-wrap gap-2">
            <button
              type="button"
              class="btn btn-secondary"
              :data-action="`verify-${domain.id}`"
              :disabled="busy"
              @click="verify(domain)">
              Check now
            </button>
            <button
              v-if="canPromote(domain)"
              type="button"
              class="btn btn-secondary"
              :data-action="`promote-${domain.id}`"
              :disabled="busy"
              @click="prepare('promote', domain)">
              Make main address
            </button>
            <button
              v-if="!domain.primary"
              type="button"
              class="btn btn-secondary"
              :data-action="`remove-${domain.id}`"
              :disabled="busy"
              @click="prepare('remove', domain)">
              Remove
            </button>
          </div>
          <div
            v-if="pending && pending.domain.id === domain.id"
            class="rounded border p-3 space-y-2"
            style="border-color: var(--border-primary, #e5e7eb);"
            data-test="confirm-panel">
            <p v-if="pending.state === 'checking'">
              Checking which pages use this address…
            </p>
            <template v-else-if="pending.state === 'ready'">
              <p>
                {{ pending.action === 'promote' ? `Make ${domain.hostname} the main address?` : `Remove ${domain.hostname}?` }}
                This changes the address for {{ pending.pages }} page(s).
              </p>
              <div class="flex flex-wrap gap-2">
                <button type="button" class="btn btn-primary" data-action="confirm" :disabled="busy" @click="confirm()">
                  {{ pending.action === 'promote' ? 'Make main address' : 'Remove domain' }}
                </button>
                <button type="button" class="btn btn-secondary" data-action="cancel" @click="pending = null">
                  Cancel
                </button>
              </div>
            </template>
          </div>
        </li>
      </ul>

      <form v-if="!readOnly" class="grid gap-2 sm:grid-cols-3" data-test="claim-form" @submit.prevent="claim()">
        <label class="flex flex-col text-sm sm:col-span-2">
          <span class="font-medium" style="color: var(--text-secondary);">Domain</span>
          <input
            v-model="hostname"
            data-test="new-hostname"
            type="text"
            class="form-input mt-1"
            placeholder="www.yourbusiness.com"
            autocomplete="off"
            :aria-invalid="hostnameError ? 'true' : 'false'"
            aria-describedby="new-hostname-error">
          <span v-if="hostnameError" id="new-hostname-error" class="text-xs text-red-700" data-test="hostname-error">
            {{ hostnameError }}
          </span>
        </label>
        <label class="flex flex-col text-sm">
          <span class="font-medium" style="color: var(--text-secondary);">Use as</span>
          <select v-model="role" data-test="new-role" class="form-select mt-1">
            <option value="primary_candidate">Main address</option>
            <option value="alias">Additional address</option>
          </select>
        </label>
        <div class="sm:col-span-3">
          <button type="submit" class="btn btn-primary" :disabled="busy || !hostname.trim()">
            Add domain
          </button>
        </div>
      </form>
    </section>

    <p role="status" aria-live="polite" class="text-sm" data-test="domain-status">
      {{ notice }}
    </p>
    <p v-if="problem" role="alert" class="text-sm text-red-700" data-test="domain-error">
      {{ problem }}
    </p>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { waitForOperation } from '~/javascript/services/landing_pages/api'

const props = defineProps({
  api: { type: Object, required: true },
  site: { type: Object, required: true },
  readOnly: { type: Boolean, default: false },
  pollIntervalMs: { type: Number, default: 1500 }
})
const emit = defineEmits(['changed'])

const STATE_LABELS = {
  pending: 'Waiting for DNS',
  active: 'Active',
  failed: 'Needs attention',
  rejected: 'Refused by hosting',
  provider_outcome_unknown: 'Confirming with hosting'
}
const STEP_LABELS = { active: 'done', pending: 'waiting', failed: 'failed' }

const hostname = ref('')
const role = ref('primary_candidate')
const hostnameError = ref('')
const busy = ref(false)
const notice = ref('')
const problem = ref('')
const pending = ref(null)

// The Made.ly default address is Made.ly-managed custody, not a customer
// domain: it is reported on the address card and never offered for removal.
const defaultDomain = computed(() => (props.site.domains || []).find((domain) => domain.role === 'default') || null)
const domains = computed(() => (props.site.domains || []).filter((domain) => domain.role !== 'default'))
const pages = computed(() => props.site.pages || [])
const hostingMessage = computed(() => {
  if (props.site.domain_state === 'hosting_not_provisioned') {
    return 'Made.ly is preparing hosting for this site; your address becomes active once it is ready.'
  }
  if (props.site.domain_state === 'pending') return 'Waiting for hosting.'
  if (defaultDomain.value && defaultDomain.value.state !== 'active') {
    return 'Made.ly is still connecting this address. Published pages are served from your hosting address until it is ready.'
  }
  return 'This address is active.'
})

function stepLabel(state) {
  return STEP_LABELS[state] || 'unknown'
}

function badgeClass(state) {
  if (state === 'active') return 'bg-green-100 text-green-800'
  if (state === 'failed' || state === 'rejected') return 'bg-red-100 text-red-800'
  return 'bg-amber-100 text-amber-900'
}

function canPromote(domain) {
  return !domain.primary && domain.state === 'active' && ['primary_candidate', 'default'].includes(domain.role)
}

function explain(error) {
  const code = error?.status === 0 ? 'outcome_unknown' : error?.code
  if (code === 'version_conflict') {
    emit('changed')
    return 'Domain settings changed; reloaded. Review and try again.'
  }
  if (code === 'outcome_unknown') return "We couldn't confirm the result. Check the domain status before trying again."
  if (code === 'provider_unavailable' || code === 'rate_limited') return 'Hosting is temporarily unavailable. Try again in a moment.'
  if (code === 'impact_unavailable') return 'Page usage could not be checked right now. Try again shortly.'
  if (code === 'domain_not_ready') return 'Hosting for this site is not ready for domains yet.'
  if (code === 'active_assignments') return 'Something still depends on this address, so it cannot change yet.'
  return 'That did not work. Nothing was changed; try again.'
}

async function settled(response) {
  let operation = response.data
  if (operation && !['succeeded', 'failed'].includes(operation.state) && operation.id) {
    operation = await waitForOperation(props.api, operation.id, { intervalMs: props.pollIntervalMs })
  }
  return operation
}

async function claim() {
  hostnameError.value = ''
  problem.value = ''
  notice.value = ''
  busy.value = true
  try {
    const result = await props.api.claimDomain(props.site.id, { hostname: hostname.value.trim(), role: role.value })
    notice.value = `${result.data?.hostname || hostname.value.trim()} added. Add the DNS record shown, then check it.`
    hostname.value = ''
    emit('changed')
  } catch (error) {
    const fields = error?.fields || {}
    const messages = fields.hostname || fields.role
    if (messages && messages.length) {
      hostnameError.value = `This domain ${messages.join(', ')}.`
    } else {
      problem.value = explain(error)
    }
  } finally {
    busy.value = false
  }
}

async function verify(domain) {
  problem.value = ''
  notice.value = ''
  busy.value = true
  try {
    const operation = await settled(await props.api.verifyDomain(props.site.id, domain.id, props.site.domain_version))
    if (operation?.state === 'succeeded') {
      notice.value = operation.result?.ready ?
        `${domain.hostname} is verified and active.` :
        `${domain.hostname} is not ready yet. DNS changes can take a while; check again later.`
      emit('changed')
    } else {
      problem.value = "We couldn't confirm the check. Try again shortly."
    }
  } catch (error) {
    problem.value = explain(error)
  } finally {
    busy.value = false
  }
}

async function prepare(action, domain) {
  problem.value = ''
  notice.value = ''
  pending.value = { action, domain, state: 'checking', digest: null, pages: 0 }
  try {
    const result = await props.api.observeRegistry(props.site.id)
    if (!result.data?.digest) throw new Error('usage unavailable')
    pending.value = { action, domain, state: 'ready', digest: result.data.digest, pages: (result.data.pages || []).length }
  } catch (error) {
    pending.value = null
    problem.value = explain(error?.code ? error : { code: 'impact_unavailable' })
  }
}

async function confirm() {
  const current = pending.value
  if (!current?.digest) return
  busy.value = true
  problem.value = ''
  try {
    const command = current.action === 'promote' ? props.api.promoteDomain : props.api.removeDomain
    const operation = await settled(await command(props.site.id, current.domain.id, props.site.domain_version, current.digest))
    pending.value = null
    if (operation?.state === 'succeeded') {
      notice.value = current.action === 'promote' ?
        `${current.domain.hostname} is now the main address.` :
        `${current.domain.hostname} was removed.`
      emit('changed')
    } else {
      problem.value = "We couldn't confirm the change. Check the domain status before trying again."
    }
  } catch (error) {
    pending.value = null
    problem.value = explain(error)
  } finally {
    busy.value = false
  }
}
</script>
